import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import axios from 'axios';
import { toast } from 'react-toastify';
import { createSafeHtml } from '../utils/sanitize';
import moment from 'moment';
import Footer from './Footer';
import Navbar from './Navbar';
import TopBar from './TopBar';

function Blogdetails() {
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [csrfToken, setCsrfToken] = useState('');
  const [formData, setFormData] = useState({ name: '', email: '', comment: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const { id } = useParams();

  const externalApiUrl =
    process.env.REACT_APP_EXTERNAL_API_URL || 'https://admin.icbfinexperts.com';

  useEffect(() => {
    const fetchPostDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get(`${externalApiUrl}/post_detail.php`, {
          params: { nid: id },
        });

        if (!response.data.error) {
          setPost(response.data.post);
          setComments(response.data.comments || []);
          setCsrfToken(response.data.csrf_token || '');
        } else {
          setError(response.data.error);
          toast.error(response.data.error);
        }
      } catch (err) {
        const errorMsg = 'Failed to load post details. Please try again later.';
        setError(errorMsg);
        toast.error(errorMsg);
        console.error('Error fetching post details:', err);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchPostDetails();
    }
  }, [id, externalApiUrl]);

  const handleChange = e => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setSubmitLoading(true);

    try {
      const response = await axios.post(
        `${externalApiUrl}/post_comment.php`,
        {
          csrftoken: csrfToken,
          id: id,
          ...formData,
        },
        {
          headers: { 'Content-Type': 'application/json' },
          withCredentials: true,
        }
      );

      if (response.data.success) {
        toast.success('Comment submitted successfully!');
        setFormData({ name: '', email: '', comment: '' });
        // Optionally refresh comments
        const updatedResponse = await axios.get(`${externalApiUrl}/post_detail.php`, {
          params: { nid: id },
        });
        setComments(updatedResponse.data.comments || []);
      } else {
        toast.error(response.data.error || 'Error submitting comment');
      }
    } catch (err) {
      toast.error('Network error. Please try again.');
      console.error('Error submitting comment:', err);
    } finally {
      setSubmitLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <TopBar />
        <main className="page-blog-details">
        <div className="container mt-4">
          <Navbar />
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: '400px' }}
          >
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
        </main>
        <Footer />
      </>
    );
  }

  if (error || !post) {
    return (
      <>
        <TopBar />
        <main className="page-blog-details">
        <div className="container mt-4">
          <Navbar />
          <div className="alert alert-danger" role="alert">
            <h4 className="alert-heading">Error</h4>
            <p>{error || 'Post not found.'}</p>
            <a href="/blog" className="btn btn-primary">
              Back to Blog
            </a>
          </div>
        </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <TopBar />
      <main className="page-blog-details">
      <div className="container mt-4">
        <Navbar />
        <section className="page-header">
          <h1>Blog Details</h1>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb foi-breadcrumb">
              <li className="breadcrumb-item">
                <a href="/">Home</a>
              </li>
              <li className="breadcrumb-item">
                <a href="/blog">Blog</a>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                Details
              </li>
            </ol>
          </nav>
        </section>
        <section className="blog-details-shell">
        <h2>{post.posttitle}</h2>

        <div>
          <span className="badge bg-secondary">{post.category}</span>{' '}
          <span className="badge bg-secondary">{post.subcategory}</span>
        </div>

        <p>
          <b>Posted by:</b> {post.postedBy} on {post.postingdate}
          {post.lastUpdatedBy && (
            <>
              <br />
              <b>Last Updated by:</b> {post.lastUpdatedBy} on {post.UpdationDate}
            </>
          )}
        </p>

        <img
          src={`${externalApiUrl}/postimages/${post.PostImage}`}
          alt={post.posttitle}
          className="img-fluid rounded"
        />

        <div
          dangerouslySetInnerHTML={createSafeHtml(post.postdetails || '')}
          style={{ marginTop: '1rem' }}
        />

        <hr />

        <h4>Comments</h4>
        {comments.length === 0 && <p>No comments yet.</p>}
        {comments.map((c, i) => (
          <div key={i} className="media mb-3">
            <img
              className="d-flex mr-3 rounded-circle"
              src="images/usericon.png"
              alt="User icon"
              style={{ width: '50px', height: '50px' }}
            />
            <div className="media-body">
              <h5 className="mt-0">{c.name}</h5>
              <small>{new Date(c.postingDate).toLocaleString()}</small>
              <p>{c.comment}</p>
            </div>
          </div>
        ))}

        <hr />

        <h5>Leave a Comment</h5>
        <form onSubmit={handleSubmit}>
          <input type="hidden" name="csrftoken" value={csrfToken} />
          <div className="mb-3">
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="form-control"
              placeholder="Enter your fullname"
              required
              disabled={submitLoading}
            />
          </div>
          <div className="mb-3">
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="form-control"
              placeholder="Enter your valid email"
              required
              disabled={submitLoading}
            />
          </div>
          <div className="mb-3">
            <textarea
              name="comment"
              value={formData.comment}
              onChange={handleChange}
              className="form-control"
              rows="3"
              placeholder="Comment"
              required
              disabled={submitLoading}
            />
          </div>
          <button type="submit" className="btn btn-primary" disabled={submitLoading}>
            {submitLoading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Submitting...
              </>
            ) : (
              'Submit'
            )}
          </button>
        </form>
        </section>
      </div>
      </main>
      <Footer />
    </>
  );
}

export default Blogdetails;
